import { useEffect, useState } from "react";
import { Download, FileImage, FileText, FileVideo, X } from "lucide-react";

import { knowledgeApi } from "../api/knowledgeApi";

interface DocumentViewerProps {
  document: {
    uuid: string;
    name: string;
    category: string;
  };
  isOpen: boolean;
  onClose: () => void;
}

export function DocumentViewer({
  document,
  isOpen,
  onClose,
}: DocumentViewerProps) {
  const [signedUrl, setSignedUrl] = useState<string>("");
  const [fileContent, setFileContent] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (isOpen && document) {
      loadDocument();
    }
  }, [isOpen, document]);

  const loadDocument = async () => {
    try {
      setLoading(true);
      setError("");
      const filePath = document.name;
      const url = await knowledgeApi.getSignedUrl(filePath);
      setSignedUrl(url);

      // If it's a text file, fetch the content
      if (isTextFile()) {
        await loadTextContent(url);
      }
    } catch (err) {
      setError("Failed to load document");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadTextContent = async (url: string) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const text = await response.text();
      setFileContent(text);
    } catch (err) {
      console.error("Failed to load text content:", err);
      setError("Failed to load text content");
    }
  };

  const getFileIcon = () => {
    const extension = document.name.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return <FileText className="size-6" />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <FileImage className="size-6" />;
      case "mp4":
      case "avi":
      case "mov":
        return <FileVideo className="size-6" />;
      case "txt":
      case "md":
        return <FileText className="size-6" />;
      default:
        return <FileText className="size-6" />;
    }
  };

  const isTextFile = () => {
    const extension = document.name.split(".").pop()?.toLowerCase();
    return ["txt", "md"].includes(extension || "");
  };

  const canPreviewInBrowser = () => {
    const extension = document.name.split(".").pop()?.toLowerCase();
    return [
      "pdf",
      "jpg",
      "jpeg",
      "png",
      "gif",
      "mp4",
      "webm",
      "txt",
      "md",
    ].includes(extension || "");
  };

  const formatTextContent = (content: string) => {
    // Add line numbers and basic formatting
    const lines = content.split("\n");
    return lines.map((line, index) => (
      <div key={index} className="flex">
        <span className="text-gray-400 text-xs w-12 flex-shrink-0 select-none">
          {index + 1}
        </span>
        <span className="text-gray-800 font-mono text-sm">
          {line || "\u00A0"} {/* Use non-breaking space for empty lines */}
        </span>
      </div>
    ));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative h-[90vh] w-[90vw] max-w-6xl rounded-lg bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            {getFileIcon()}
            <div>
              <h2 className="text-lg font-semibold">{document.name}</h2>
              <p className="text-sm text-gray-500 capitalize">
                {document.category}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.open(signedUrl, "_blank")}
              className="flex items-center gap-2 rounded-md bg-blue-500 px-3 py-2 text-white hover:bg-blue-600"
            >
              <Download className="size-4" />
              Download
            </button>
            <button
              onClick={onClose}
              className="rounded-md p-2 hover:bg-gray-100"
            >
              <X className="size-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 min-h-0">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                <p>Loading document...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center text-red-500">
                <p>{error}</p>
                <button
                  onClick={loadDocument}
                  className="mt-2 rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : canPreviewInBrowser() ? (
            <div className="h-full">
              {document.name.toLowerCase().endsWith(".pdf") ? (
                <iframe
                  src={signedUrl}
                  className="h-full w-full rounded border"
                  title={document.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                />
              ) : document.name
                  .toLowerCase()
                  .match(/\.(jpg|jpeg|png|gif)$/i) ? (
                <div className="flex h-full items-center justify-center">
                  <img
                    src={signedUrl}
                    alt={document.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              ) : document.name.toLowerCase().match(/\.(mp4|webm)$/i) ? (
                <video
                  src={signedUrl}
                  controls
                  className="h-full w-full rounded border"
                >
                  Your browser does not support the video tag.
                </video>
              ) : isTextFile() ? (
                <div className="h-full flex flex-col">
                  {/* Text file header */}
                  <div className="flex items-center justify-between mb-2 p-2 bg-gray-50 rounded flex-shrink-0">
                    <span className="text-sm text-gray-600">
                      {document.name.split(".").pop()?.toUpperCase()} •{" "}
                      {fileContent.split("\n").length} lines
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(fileContent);
                        // You could add a toast notification here
                      }}
                      className="text-xs text-blue-500 hover:underline"
                    >
                      Copy All
                    </button>
                  </div>

                  {/* Text content */}
                  <div className="flex-1 overflow-auto bg-gray-50 rounded border min-h-0">
                    <div className="p-4">
                      {fileContent ? (
                        <div className="space-y-0">
                          {formatTextContent(fileContent)}
                        </div>
                      ) : (
                        <div className="text-gray-500 text-center py-8">
                          No content to display
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex h-full items-center justify-center">
                  <p className="text-gray-500">
                    Preview not available for this file type
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <p className="text-gray-500 mb-4">
                  This file type cannot be previewed in the browser
                </p>
                <button
                  onClick={() => window.open(signedUrl, "_blank")}
                  className="flex items-center gap-2 rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 mx-auto"
                >
                  <Download className="size-4" />
                  Download to View
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
